import type { EventMapPoint } from '@racemap/utilities/api-client';

export type EventStatus = 'live' | 'upcoming' | 'past';

type Timed = { startTime: Date | string; endTime: Date | string };

const ms = (d: Date | string) => new Date(d).getTime();

export function statusOf(event: Timed, now = Date.now()): EventStatus {
  if (ms(event.startTime) > now) return 'upcoming';
  return ms(event.endTime) < now ? 'past' : 'live';
}

/** The first section that still has pages to load, or null when everything is loaded. */
export function activeSection<K extends string>(
  sections: Array<{ key: K; exhausted: boolean }>,
): K | null {
  return sections.find((s) => !s.exhausted)?.key ?? null;
}

const fmt = (d: Date, timeZone: string | undefined, opts: Intl.DateTimeFormatOptions) =>
  d.toLocaleString('en-GB', { timeZone, ...opts });

export function dateRangeLabel(event: Timed, timeZone?: string): string {
  const start = new Date(event.startTime);
  const end = new Date(event.endTime);
  const full = { day: 'numeric', month: 'short', year: 'numeric' } as const;
  if (fmt(start, timeZone, full) === fmt(end, timeZone, full)) {
    return `${fmt(start, timeZone, full)}, ${fmt(start, timeZone, { hour: '2-digit', minute: '2-digit' })}`;
  }
  const sameYear =
    fmt(start, timeZone, { year: 'numeric' }) === fmt(end, timeZone, { year: 'numeric' });
  const from = fmt(start, timeZone, sameYear ? { day: 'numeric', month: 'short' } : full);
  return `${from} – ${fmt(end, timeZone, full)}`;
}

const HOUR = 3600_000;
const DAY = 24 * HOUR;

const inUnits = (diff: number) =>
  diff < DAY ? `${Math.max(1, Math.round(diff / HOUR))} h` : `${Math.round(diff / DAY)} d`;

export function relativeLabel(event: Timed, now = Date.now()): string {
  const status = statusOf(event, now);
  if (status === 'live') return `ends in ${inUnits(ms(event.endTime) - now)}`;
  if (status === 'upcoming') return `in ${inUnits(ms(event.startTime) - now)}`;
  return '';
}

/** Returns the URL only when it is https, so a stored `javascript:` link never reaches an href. */
export function httpsOnly(url: string | null | undefined): string | null {
  if (url == null || !URL.canParse(url)) return null;
  return new URL(url).protocol === 'https:' ? url : null;
}

/** The extent of the located events, `[[west, south], [east, north]]`, or null when there are none. */
export function pointsBounds(
  points: Array<EventMapPoint>,
): [[number, number], [number, number]] | null {
  if (points.length === 0) return null;
  const lats = points.map((p) => p.lat);
  const south = Math.min(...lats);
  const north = Math.max(...lats);

  // The tightest arc holding every longitude is the one opposite the widest empty stretch; west past
  // east wraps it. constrainToWorld can clip the outer edge of a seam-centred view at low zoom.
  const lngs = points.map((p) => p.lng).sort((a, b) => a - b);
  let widest = lngs[0] + 360 - lngs[lngs.length - 1];
  let start = 0;
  for (let i = 1; i < lngs.length; i += 1) {
    const gap = lngs[i] - lngs[i - 1];
    if (gap > widest) {
      widest = gap;
      start = i;
    }
  }

  return [
    [lngs[start], south],
    [lngs[(start - 1 + lngs.length) % lngs.length], north],
  ];
}
